import React from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Skeleton, alpha,
} from '@mui/material';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import { useTranslation } from 'react-i18next';
import { getScoreColor } from '../../theme';

const STRATEGY_COLORS = {
  ONEIL: '#3b82f6', MINERVINI: '#8b5cf6', WEINSTEIN: '#10b981',
  TURTLE: '#f97316', LIVERMORE: '#ef4444',
};

const buildStrengthCfg = (t) => ({
  strong:   { label: t('legendary.signalTable.strength.strong', 'Strong'),     color: '#10b981', bg: 'rgba(16,185,129,0.08)' },
  moderate: { label: t('legendary.signalTable.strength.moderate', 'Moderate'), color: '#f59e0b', bg: 'rgba(245,158,11,0.08)' },
  weak:     { label: t('legendary.signalTable.strength.weak', 'Weak'),         color: '#94a3b8', bg: 'rgba(148,163,184,0.08)' },
});

const fmt = (n) => n != null ? n.toLocaleString('vi-VN') : '—';

// Color-code reason chips by category
const REASON_CHIP_STYLES = {
  // Breakout / pivot
  breakout: { bg: '#ede9fe', color: '#7c3aed' },
  pivot: { bg: '#ede9fe', color: '#7c3aed' },
  // Volume
  volume: { bg: '#dbeafe', color: '#2563eb' },
  kl: { bg: '#dbeafe', color: '#2563eb' },
  // Price / MA
  ma: { bg: '#d1fae5', color: '#059669' },
  sma: { bg: '#d1fae5', color: '#059669' },
  trend: { bg: '#d1fae5', color: '#059669' },
  // Strength
  strong: { bg: '#fef3c7', color: '#b45309' },
  close: { bg: '#fef3c7', color: '#b45309' },
  // Channel / Donchian
  donchian: { bg: '#ffedd5', color: '#c2410c' },
  channel: { bg: '#ffedd5', color: '#c2410c' },
  // ATR
  atr: { bg: '#fce7f3', color: '#be185d' },
  risk: { bg: '#fce7f3', color: '#be185d' },
};

const getReasonChipStyle = (reason) => {
  if (!reason) return { bg: '#f1f5f9', color: '#475569' };
  const lower = reason.toLowerCase();
  for (const [key, style] of Object.entries(REASON_CHIP_STYLES)) {
    if (lower.includes(key)) return style;
  }
  return { bg: '#f1f5f9', color: '#475569' };
};

// Translate reason key using i18n, with fallback for dynamic patterns
const translateReason = (reason, t) => {
  if (!reason) return reason;
  // Exact match from locale
  const key = `legendary.reasons.${reason}`;
  const translated = t(key, { defaultValue: '' });
  if (translated && translated !== key) return translated;
  // Dynamic pattern matches
  if (reason.startsWith('in_buy_zone_')) return t('legendary.reasons.in_buy_zone');
  if (reason.startsWith('extended_') && reason.includes('above_pivot')) return t('legendary.reasons.extended_above_pivot');
  if (reason.startsWith('overextended_sma50_')) return t('legendary.reasons.overextended_sma50');
  if (reason.startsWith('donchian_') && reason.includes('_entry')) return t('legendary.reasons.donchian_entry');
  if (reason.startsWith('chasing_above_channel_')) return t('legendary.reasons.chasing_above_channel');
  if (reason.startsWith('atr_pct=')) return `ATR ${reason.split('=')[1]}`;
  if (reason.startsWith('vcp:')) return `VCP: ${reason.split(':')[1].trim()}`;
  return reason;
};

export default function SignalTable({ signals = [], loading = false, showStrategy = false }) {
  const { t } = useTranslation();
  const strengthCfg = buildStrengthCfg(t);

  if (loading) {
    const headSx = {
      fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase',
      letterSpacing: '0.04em', color: '#64748b',
      bgcolor: '#fafbfc', borderBottom: '2px solid #e2e8f0', py: 1.2,
    };
    return (
      <TableContainer component={Paper} elevation={0} sx={{
        border: '1px solid', borderColor: 'divider', borderRadius: 2,
      }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headSx, width: 36 }} align="center">{t('legendary.signalTable.col.rank', '#')}</TableCell>
              <TableCell sx={{ ...headSx, width: 90 }}>{t('legendary.signalTable.col.symbol', 'Symbol')}</TableCell>
              {showStrategy && <TableCell sx={{ ...headSx, width: 100 }}>{t('legendary.signalTable.col.strategy', 'Strategy')}</TableCell>}
              <TableCell sx={{ ...headSx, width: 72 }} align="center">{t('legendary.signalTable.col.score', 'Score')}</TableCell>
              <TableCell sx={{ ...headSx, width: 90 }} align="center">{t('legendary.signalTable.col.strength', 'Strength')}</TableCell>
              <TableCell sx={{ ...headSx, width: 85 }} align="right">{t('legendary.signalTable.col.entry', 'Entry')}</TableCell>
              <TableCell sx={{ ...headSx, width: 85 }} align="right">{t('legendary.signalTable.col.stop', 'Stop')}</TableCell>
              <TableCell sx={{ ...headSx, width: 85 }} align="right">{t('legendary.signalTable.col.target', 'Target')}</TableCell>
              <TableCell sx={{ ...headSx, width: 65 }} align="right">{t('legendary.signalTable.col.riskPct', 'Risk %')}</TableCell>
              <TableCell sx={{ ...headSx, width: 65 }} align="center">{t('legendary.signalTable.col.rr', 'R:R')}</TableCell>
              <TableCell sx={{ ...headSx, width: 50 }} align="center">{t('legendary.signalTable.col.rs', 'RS')}</TableCell>
              <TableCell sx={{ ...headSx, width: 65 }} align="center">{t('legendary.signalTable.col.conviction', 'Conviction')}</TableCell>
              <TableCell sx={{ ...headSx, width: 50 }} align="center">{t('legendary.signalTable.col.size', 'Size %')}</TableCell>
              <TableCell sx={{ ...headSx, width: 60 }} align="right">{t('legendary.signalTable.col.gain', 'Gain %')}</TableCell>
              <TableCell sx={headSx}>{t('legendary.signalTable.col.details', 'Details')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <TableRow key={i} sx={{ '& td': { borderBottom: '1px solid #f1f5f9', py: 1.2 } }}>
                <TableCell align="center"><Skeleton variant="circular" width={22} height={22} /></TableCell>
                <TableCell><Skeleton width={48} height={18} sx={{ borderRadius: 0.5 }} /></TableCell>
                {showStrategy && <TableCell><Skeleton variant="rounded" width={64} height={22} sx={{ borderRadius: 1 }} /></TableCell>}
                <TableCell align="center"><Skeleton variant="rounded" width={38} height={24} sx={{ borderRadius: 1, mx: 'auto' }} /></TableCell>
                <TableCell align="center"><Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: 1, mx: 'auto' }} /></TableCell>
                <TableCell align="right"><Skeleton width={55} height={18} sx={{ ml: 'auto', borderRadius: 0.5 }} /></TableCell>
                <TableCell align="right"><Skeleton width={55} height={18} sx={{ ml: 'auto', borderRadius: 0.5 }} /></TableCell>
                <TableCell align="right"><Skeleton width={55} height={18} sx={{ ml: 'auto', borderRadius: 0.5 }} /></TableCell>
                <TableCell align="right"><Skeleton width={40} height={18} sx={{ ml: 'auto', borderRadius: 0.5 }} /></TableCell>
                <TableCell align="center"><Skeleton width={36} height={18} sx={{ mx: 'auto', borderRadius: 0.5 }} /></TableCell>
                <TableCell align="center"><Skeleton width={28} height={18} sx={{ mx: 'auto', borderRadius: 0.5 }} /></TableCell>
                <TableCell align="center"><Skeleton width={36} height={18} sx={{ mx: 'auto', borderRadius: 0.5 }} /></TableCell>
                <TableCell align="center"><Skeleton width={30} height={18} sx={{ mx: 'auto', borderRadius: 0.5 }} /></TableCell>
                <TableCell align="right"><Skeleton width={40} height={18} sx={{ ml: 'auto', borderRadius: 0.5 }} /></TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Skeleton variant="rounded" width={60} height={20} sx={{ borderRadius: 1 }} />
                    <Skeleton variant="rounded" width={50} height={20} sx={{ borderRadius: 1 }} />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  if (!signals || signals.length === 0) {
    return (
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 5, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {t('legendary.signalTable.empty', 'No signals yet. Click "Scan" to start.')}
        </Typography>
      </Paper>
    );
  }

  const headSx = {
    fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase',
    letterSpacing: '0.05em', color: '#94a3b8',
    bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', py: 1,
    whiteSpace: 'nowrap',
  };

  // Compute summary stats
  const avgScore = signals.length > 0 ? (signals.reduce((s, x) => s + (x.score || 0), 0) / signals.length) : 0;
  const strongPct = signals.length > 0
    ? Math.round(signals.filter(x => (x.signal_strength || x.strength || '').toLowerCase() === 'strong').length / signals.length * 100)
    : 0;
  const avgRR = signals.filter(x => x.risk_reward).length > 0
    ? (signals.filter(x => x.risk_reward).reduce((s, x) => s + x.risk_reward, 0) / signals.filter(x => x.risk_reward).length)
    : 0;

  const newCount = signals.filter(x => x.is_new).length;
  const avgConviction = signals.length > 0 && signals[0].conviction_score != null
    ? (signals.reduce((s, x) => s + (x.conviction_score || 0), 0) / signals.length) : null;

  return (
    <Box>
      {/* ── Summary strip ────────── */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 3, mb: 1.5, px: 0.5,
      }}>
        {[
          { label: t('legendary.signalTable.summary.signals', 'Signals'), value: signals.length, color: '#334155' },
          ...(newCount > 0 ? [{ label: t('legendary.signalTable.summary.new', 'New'), value: newCount, color: '#10b981' }] : []),
          { label: t('legendary.signalTable.summary.avgScore', 'Avg Score'), value: avgScore.toFixed(0), color: avgScore >= 85 ? '#10b981' : avgScore >= 75 ? '#3b82f6' : '#f59e0b' },
          ...(avgConviction != null ? [{ label: t('legendary.signalTable.summary.conviction', 'Conviction'), value: avgConviction.toFixed(0), color: avgConviction >= 70 ? '#10b981' : '#3b82f6' }] : []),
          { label: t('legendary.signalTable.summary.strongPct', 'Strong %'), value: `${strongPct}%`, color: strongPct >= 80 ? '#10b981' : '#64748b' },
          { label: t('legendary.signalTable.summary.avgRR', 'Avg R:R'), value: avgRR.toFixed(1), color: avgRR >= 2 ? '#10b981' : '#f59e0b' },
        ].map((m, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {m.label}
            </Typography>
            <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, fontFamily: 'monospace', color: m.color }}>
              {m.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{
        maxHeight: 640, borderRadius: 2, overflow: 'hidden',
        border: '1px solid', borderColor: '#e5e7eb',
      }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headSx, width: 36 }} align="center">{t('legendary.signalTable.col.rank', '#')}</TableCell>
            <TableCell sx={{ ...headSx, width: 90 }}>{t('legendary.signalTable.col.symbol', 'Symbol')}</TableCell>
            {showStrategy && <TableCell sx={{ ...headSx, width: 100 }}>{t('legendary.signalTable.col.strategy', 'Strategy')}</TableCell>}
            <TableCell sx={{ ...headSx, width: 72 }} align="center">{t('legendary.signalTable.col.score', 'Score')}</TableCell>
            <TableCell sx={{ ...headSx, width: 90 }} align="center">{t('legendary.signalTable.col.strength', 'Strength')}</TableCell>
            <TableCell sx={{ ...headSx, width: 85 }} align="right">{t('legendary.signalTable.col.entry', 'Entry')}</TableCell>
            <TableCell sx={{ ...headSx, width: 85 }} align="right">{t('legendary.signalTable.col.stop', 'Stop')}</TableCell>
            <TableCell sx={{ ...headSx, width: 85 }} align="right">{t('legendary.signalTable.col.target', 'Target')}</TableCell>
            <TableCell sx={{ ...headSx, width: 75, whiteSpace: 'nowrap' }} align="right">{t('legendary.signalTable.col.riskPct', 'Risk %')}</TableCell>
            <TableCell sx={{ ...headSx, width: 65 }} align="center">{t('legendary.signalTable.col.rr', 'R:R')}</TableCell>
            <TableCell sx={{ ...headSx, width: 50 }} align="center">{t('legendary.signalTable.col.rs', 'RS')}</TableCell>
            <TableCell sx={{ ...headSx, width: 65 }} align="center">{t('legendary.signalTable.col.conviction', 'Conviction')}</TableCell>
            <TableCell sx={{ ...headSx, width: 50 }} align="center">{t('legendary.signalTable.col.size', 'Size %')}</TableCell>
            <TableCell sx={{ ...headSx, width: 60 }} align="right">{t('legendary.signalTable.col.gain', 'Gain %')}</TableCell>
            <TableCell sx={headSx}>{t('legendary.signalTable.col.details', 'Details')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {signals.map((s, idx) => {
            const sc = getScoreColor(s.score_level);
            const st = strengthCfg[(s.signal_strength || s.strength).toLowerCase()] || strengthCfg.weak;
            const stratColor = STRATEGY_COLORS[s.strategy] || '#64748b';

            return (
              <TableRow key={`${s.symbol}-${s.strategy}-${idx}`} sx={{
                '&:hover': { bgcolor: alpha(stratColor, 0.03) },
                '& td': { borderBottom: '1px solid #f1f5f9', py: 1 },
                bgcolor: idx % 2 === 0 ? 'transparent' : '#fafbfd',
              }}>
                {/* Rank */}
                <TableCell align="center">
                  <Box sx={{
                    width: 22, height: 22, borderRadius: '50%', display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center', fontFamily: 'monospace',
                    fontSize: '0.68rem', fontWeight: 700,
                    bgcolor: idx < 3 ? alpha(sc, 0.10) : '#f1f5f9',
                    color: idx < 3 ? sc : '#94a3b8',
                  }}>
                    {idx + 1}
                  </Box>
                </TableCell>

                {/* Symbol + NEW badge */}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.85rem', color: '#1e293b' }}>
                      {s.symbol}
                    </Typography>
                    {s.is_new && (
                      <FiberNewIcon sx={{ fontSize: 16, color: '#10b981' }} />
                    )}
                  </Box>
                </TableCell>

                {/* Strategy */}
                {showStrategy && (
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: stratColor, flexShrink: 0 }} />
                      <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.72rem', color: stratColor }}>
                        {s.strategy}
                      </Typography>
                    </Box>
                  </TableCell>
                )}

                {/* Score with bar */}
                <TableCell align="center">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
                    <Typography variant="body2" sx={{
                      fontWeight: 800, fontSize: '0.85rem', fontFamily: 'monospace', color: sc, lineHeight: 1,
                    }}>
                      {s.score?.toFixed?.(0) ?? s.score}
                    </Typography>
                    <Box sx={{ width: 32, height: 3, borderRadius: 1.5, bgcolor: alpha(sc, 0.15), overflow: 'hidden' }}>
                      <Box sx={{ width: `${Math.min((s.score || 0), 100)}%`, height: '100%', borderRadius: 1.5, bgcolor: sc }} />
                    </Box>
                  </Box>
                </TableCell>

                {/* Strength */}
                <TableCell align="center">
                  <Typography variant="caption" sx={{
                    fontWeight: 700, fontSize: '0.72rem', color: st.color,
                  }}>
                    <Box component="span" sx={{ display: 'inline-block', width: 5, height: 5, borderRadius: '50%', bgcolor: st.color, mr: 0.5, verticalAlign: 'middle' }} />
                    {st.label}
                  </Typography>
                </TableCell>

                {/* Entry */}
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', fontFamily: 'monospace' }}>
                    {fmt(s.entry)}
                  </Typography>
                </TableCell>

                {/* Stop */}
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', fontFamily: 'monospace', color: '#ef4444' }}>
                    {fmt(s.stop)}
                  </Typography>
                </TableCell>

                {/* Target */}
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', fontFamily: 'monospace', color: '#10b981' }}>
                    {fmt(s.target)}
                  </Typography>
                </TableCell>

                {/* Risk % */}
                <TableCell align="right">
                  {s.risk_pct != null ? (
                    <Typography variant="body2" fontWeight={600} sx={{
                      fontSize: '0.82rem', fontFamily: 'monospace',
                      color: s.risk_pct > 8 ? '#ef4444' : s.risk_pct > 5 ? '#f59e0b' : '#10b981',
                    }}>
                      {s.risk_pct.toFixed(1)}%
                    </Typography>
                  ) : <Typography variant="body2" color="text.disabled">—</Typography>}
                </TableCell>

                {/* R:R */}
                <TableCell align="center">
                  {s.risk_reward != null ? (
                    <Typography variant="body2" sx={{
                      fontWeight: 700, fontSize: '0.82rem', fontFamily: 'monospace',
                      color: s.risk_reward >= 2 ? '#10b981' : s.risk_reward >= 1 ? '#f59e0b' : '#ef4444',
                    }}>
                      {s.risk_reward.toFixed(1)}
                    </Typography>
                  ) : <Typography variant="body2" color="text.disabled">—</Typography>}
                </TableCell>

                {/* RS Percentile */}
                <TableCell align="center">
                  {s.rs_percentile != null ? (
                    <Typography variant="body2" fontWeight={700} sx={{
                      fontSize: '0.78rem', fontFamily: 'monospace',
                      color: s.rs_percentile >= 80 ? '#10b981' : s.rs_percentile >= 60 ? '#3b82f6' : '#94a3b8',
                    }}>
                      {Math.round(s.rs_percentile)}
                    </Typography>
                  ) : <Typography variant="body2" color="text.disabled">—</Typography>}
                </TableCell>

                {/* Conviction Score */}
                <TableCell align="center">
                  {s.conviction_score != null ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
                      <Typography variant="body2" fontWeight={800} sx={{
                        fontSize: '0.82rem', fontFamily: 'monospace',
                        color: s.conviction_score >= 80 ? '#10b981' : s.conviction_score >= 65 ? '#3b82f6' : '#f59e0b',
                      }}>
                        {Math.round(s.conviction_score)}
                      </Typography>
                    </Box>
                  ) : <Typography variant="body2" color="text.disabled">—</Typography>}
                </TableCell>

                {/* Position Size % */}
                <TableCell align="center">
                  {s.position_size_pct != null ? (
                    <Typography variant="body2" fontWeight={600} sx={{
                      fontSize: '0.78rem', fontFamily: 'monospace',
                      color: '#475569',
                    }}>
                      {s.position_size_pct}%
                    </Typography>
                  ) : <Typography variant="body2" color="text.disabled">—</Typography>}
                </TableCell>

                {/* Gain % (target upside from entry) */}
                <TableCell align="right">
                  {s.entry && s.target ? (() => {
                    const gain = ((s.target - s.entry) / s.entry) * 100;
                    return (
                      <Typography variant="body2" fontWeight={700} sx={{
                        fontSize: '0.78rem', fontFamily: 'monospace', color: '#10b981',
                      }}>
                        +{gain.toFixed(1)}%
                      </Typography>
                    );
                  })() : <Typography variant="body2" color="text.disabled">—</Typography>}
                </TableCell>

                {/* Reasons */}
                <TableCell>
                  <Tooltip title={
                    <Box sx={{ maxWidth: 280 }}>
                      {s.reasons?.map((r, i) => (
                        <Typography key={i} variant="caption" sx={{ display: 'block', py: 0.2 }}>{translateReason(r, t)}</Typography>
                      ))}
                    </Box>
                  } arrow placement="left">
                    <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'nowrap', cursor: 'default' }}>
                      {s.reasons?.filter(r => !r.includes('_ok') && !r.includes('volume_ok')).slice(0, 3)
                        .map((r, i) => {
                          const rs = getReasonChipStyle(r);
                          return (
                            <Box key={i} sx={{
                              px: 0.6, py: 0.15, borderRadius: 0.75, bgcolor: rs.bg,
                              display: 'inline-flex', alignItems: 'center',
                            }}>
                              <Typography variant="caption" sx={{
                                fontWeight: 600, fontSize: '0.65rem', color: rs.color,
                                whiteSpace: 'nowrap', lineHeight: 1.3,
                              }}>
                                {translateReason(r, t)}
                              </Typography>
                            </Box>
                          );
                        })}
                    </Box>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
    </Box>
  );
}
